<template>
  <section class="section section-drama">
    <div class="tech-bg">
      <div class="grid-overlay"></div>
      <div class="floating-particles">
        <div class="particle" v-for="n in 30" :key="n" :style="particleStyle(n)"></div>
      </div>
      <div class="glow-orb glow-orb-1"></div>
      <div class="glow-orb glow-orb-2"></div>
      <div class="glow-orb glow-orb-3"></div>
      <div class="hex-grid"></div>
    </div>
    <div class="container">
      <div class="page-head">
        <h2>AI 短剧视频</h2>
        <p>输入剧本，自动生成分镜、角色、场景、成片</p>
      </div>
      <div class="card">
        <div class="form-item">
          <label>剧本内容</label>
          <textarea
            v-model="form.script"
            :rows="6"
            placeholder="请输入短剧剧本内容"
            class="textarea"
          ></textarea>
        </div>
        <div class="form-row">
          <div class="form-item half">
            <label>短剧时长</label>
            <select v-model="form.duration" class="select">
              <option value="1">1分钟</option>
              <option value="3">3分钟</option>
              <option value="5">5分钟</option>
            </select>
          </div>
          <div class="form-item half">
            <label>风格类型</label>
            <select v-model="form.style" class="select">
              <option value="modern">现代</option>
              <option value="ancient">古风</option>
              <option value="fantasy">奇幻</option>
              <option value="sci-fi">科幻</option>
            </select>
          </div>
        </div>
        <div class="form-item">
          <label>角色设定</label>
          <textarea
            v-model="form.characters"
            :rows="3"
            placeholder="请描述主要角色的形象特征"
            class="textarea"
          ></textarea>
        </div>
        
        <!-- 参考图片上传 -->
        <div class="form-item">
          <label>参考图片（支持多张）</label>
          <div class="upload-area">
            <input 
              type="file" 
              ref="referenceInput" 
              multiple 
              accept="image/*" 
              style="display: none" 
              @change="handleReferenceUpload"
            >
            <div class="upload-placeholder" @click="$refs.referenceInput.click()">
              <div class="upload-icon">📁</div>
              <p>点击上传参考图片</p>
              <p class="hint">支持 JPG、PNG 格式，最多 10 张</p>
            </div>
            <div class="uploaded-images" v-if="form.references.length > 0">
              <div class="image-item" v-for="(image, index) in form.references" :key="index">
                <img :src="image" alt="参考图片" class="preview-image">
                <button class="remove-btn" @click="removeReference(index)">×</button>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 首帧和尾帧上传 -->
        <div class="form-row">
          <div class="form-item half">
            <label>首帧图片</label>
            <div class="upload-area">
              <input 
                type="file" 
                ref="startFrameInput" 
                accept="image/*" 
                style="display: none" 
                @change="handleStartFrameUpload"
              >
              <div v-if="!form.startFrame" class="upload-placeholder" @click="$refs.startFrameInput.click()">
                <div class="upload-icon">📁</div>
                <p>点击上传首帧</p>
                <p class="hint">视频开始画面</p>
              </div>
              <div v-else class="uploaded-image">
                <img :src="form.startFrame" alt="首帧图片" class="preview-image">
                <button class="remove-btn" @click="form.startFrame = null">×</button>
              </div>
            </div>
          </div>
          <div class="form-item half">
            <label>尾帧图片</label>
            <div class="upload-area">
              <input 
                type="file" 
                ref="endFrameInput" 
                accept="image/*" 
                style="display: none" 
                @change="handleEndFrameUpload"
              >
              <div v-if="!form.endFrame" class="upload-placeholder" @click="$refs.endFrameInput.click()">
                <div class="upload-icon">📁</div>
                <p>点击上传尾帧</p>
                <p class="hint">视频结束画面</p>
              </div>
              <div v-else class="uploaded-image">
                <img :src="form.endFrame" alt="尾帧图片" class="preview-image">
                <button class="remove-btn" @click="form.endFrame = null">×</button>
              </div>
            </div>
          </div>
        </div>
        <button class="btn primary" @click="generateDrama" :disabled="loading">
          {{ loading ? '生成中...' : '生成短剧' }}
        </button>
        
        <div v-if="result" class="result-section">
          <h4>生成结果</h4>
          <div class="video-container">
            <video
              :src="result.video_url"
              :poster="result.thumbnail"
              controls
              class="result-video"
            ></video>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { authMixin } from '../utils/authMixin'

export default {
  name: 'Drama',
  mixins: [authMixin],
  data() {
    return {
      form: {
        script: '',
        duration: '3',
        style: 'modern',
        characters: '',
        references: [],
        startFrame: null,
        endFrame: null
      },
      loading: false,
      result: null
    }
  },
  methods: {
    particleStyle() {
      const size = Math.random() * 4 + 1
      const left = Math.random() * 100
      const top = Math.random() * 100
      const duration = Math.random() * 20 + 10
      const delay = Math.random() * 10
      return {
        width: `${size}px`,
        height: `${size}px`,
        left: `${left}%`,
        top: `${top}%`,
        animationDuration: `${duration}s`,
        animationDelay: `${delay}s`
      }
    },
    async generateDrama() {
      if (!this.form.script) {
        alert('请输入剧本内容')
        return
      }
      
      // 准备参数
      const params = {
        prompt: this.form.script,
        model: 'GPT-4',
        duration: this.form.duration,
        genre: this.form.style
      }
      
      // 跳转到生成模块
      this.$router.push({
        path: '/drama-generator-pro',
        query: params
      })
    },
    
    handleReferenceUpload(event) {
      const files = event.target.files
      if (files.length > 0) {
        for (let i = 0; i < files.length; i++) {
          if (this.form.references.length >= 10) {
            alert('最多只能上传10张参考图片')
            break
          }
          const reader = new FileReader()
          reader.onload = (e) => {
            this.form.references.push(e.target.result)
          }
          reader.readAsDataURL(files[i])
        }
      }
    },
    
    handleStartFrameUpload(event) {
      const file = event.target.files[0]
      if (file) {
        const reader = new FileReader()
        reader.onload = (e) => {
          this.form.startFrame = e.target.result
        }
        reader.readAsDataURL(file)
      }
    },
    
    handleEndFrameUpload(event) {
      const file = event.target.files[0]
      if (file) {
        const reader = new FileReader()
        reader.onload = (e) => {
          this.form.endFrame = e.target.result
        }
        reader.readAsDataURL(file)
      }
    },
    
    removeReference(index) {
      this.form.references.splice(index, 1)
    },
    
    dataURLtoFile(dataurl, filename) {
      const arr = dataurl.split(',')
      const mime = arr[0].match(/:(.*?);/)[1]
      const bstr = atob(arr[1])
      let n = bstr.length
      const u8arr = new Uint8Array(n)
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
      }
      return new File([u8arr], filename, { type: mime })
    }
  }
}
</script>

<style scoped>
.section-drama {
  padding-top: 100px;
  overflow-y: auto;
  min-height: 100vh;
  background: linear-gradient(180deg, #0a0e1a 0%, #0d1224 50%, #0a0e1a 100%);
  position: relative;
}

.tech-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
}

.grid-overlay {
  position: absolute;
  width: 100%;
  height: 100%;
  background-image: 
    linear-gradient(rgba(0, 230, 172, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 230, 172, 0.03) 1px, transparent 1px);
  background-size: 50px 50px;
  animation: gridMove 20s linear infinite;
}

@keyframes gridMove {
  0% { transform: translate(0, 0); }
  100% { transform: translate(50px, 50px); }
}

.floating-particles {
  position: absolute;
  width: 100%;
  height: 100%;
}

.particle {
  position: absolute;
  background: rgba(0, 230, 172, 0.4);
  border-radius: 50%;
  animation: floatParticle linear infinite;
  filter: blur(1px);
  box-shadow: 0 0 8px rgba(0, 230, 172, 0.3);
}

@keyframes floatParticle {
  0% { transform: translateY(0) translateX(0); opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { transform: translateY(-100vh) translateX(50px); opacity: 0; }
}

.glow-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.15;
}

.glow-orb-1 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(0, 204, 153, 0.6) 0%, transparent 70%);
  top: 20%;
  left: 10%;
  animation: floatOrb1 15s ease-in-out infinite;
}

.glow-orb-2 {
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(0, 230, 172, 0.6) 0%, transparent 70%);
  top: 50%;
  right: 15%;
  animation: floatOrb2 18s ease-in-out infinite;
}

.glow-orb-3 {
  width: 350px;
  height: 350px;
  background: radial-gradient(circle, rgba(79, 172, 254, 0.6) 0%, transparent 70%);
  bottom: 10%;
  left: 50%;
  animation: floatOrb3 20s ease-in-out infinite;
}

@keyframes floatOrb1 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(100px, -50px); }
}

@keyframes floatOrb2 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-80px, 80px); }
}

@keyframes floatOrb3 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-60px, -100px); }
}

.hex-grid {
  position: absolute;
  width: 100%;
  height: 100%;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='49' viewBox='0 0 28 49'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%2300e6ac' fill-opacity='0.02'%3E%3Cpath d='M13.99 9.25l13 7.5v15l-13 7.5L1 31.75v-15l12.99-7.5zM3 17.9v12.7l10.99 6.34 11-6.35V17.9l-11-6.34L3 17.9zM0 15l12.98-7.5V0h-2v6.35L0 12.69v2.3zm0 18.5L12.98 41v8h-2v-6.85L0 35.81v-2.3zM15 0v7.5L27.99 15H28v-2.31h-.01L17 6.35V0h-2zm0 49v-8l12.99-7.5H28v2.31h-.01L17 42.15V49h-2z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.4;
}

.container {
  position: relative;
  z-index: 1;
}

.page-head {
  text-align: center;
  margin-bottom: 40px;
}

.page-head h2 {
  font-size: 32px;
  margin-bottom: 10px;
}

.page-head p {
  color: #888;
  font-size: 16px;
}

.card {
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 16px;
  padding: 40px;
  max-width: 900px;
  margin: 0 auto;
}

.form-item {
  margin-bottom: 20px;
}

.form-row {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.form-item.half {
  flex: 1;
}

label {
  display: block;
  margin-bottom: 8px;
  color: #aaa;
  font-size: 14px;
}

.textarea {
  width: 100%;
  padding: 12px 16px;
  background: #1a1e2d;
  border: 1px solid #333;
  border-radius: 8px;
  color: #fff;
  outline: none;
  resize: vertical;
  font-family: inherit;
}

.textarea:focus {
  border-color: #00e6ac;
}

.select {
  width: 100%;
  padding: 12px 16px;
  background: #1a1e2d;
  border: 1px solid #333;
  border-radius: 8px;
  color: #fff;
  outline: none;
}

.select:focus {
  border-color: #00e6ac;
}

.result-section {
  margin-top: 30px;
  padding-top: 30px;
  border-top: 1px solid rgba(255,255,255,0.05);
}

.result-section h4 {
  font-size: 18px;
  margin-bottom: 20px;
}

.video-container {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
}

.result-video {
  width: 100%;
  height: auto;
  max-height: 500px;
  object-fit: contain;
  border-radius: 8px;
  background: #000;
}

.upload-area {
  margin-top: 8px;
}

.upload-placeholder {
  border: 2px dashed #333;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(255,255,255,0.02);
}

.upload-placeholder:hover {
  border-color: #00e6ac;
  background: rgba(0,230,172,0.05);
}

.upload-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

.upload-placeholder p {
  margin: 8px 0;
  color: #aaa;
  font-size: 14px;
}

.upload-placeholder .hint {
  font-size: 12px;
  color: #666;
}

.uploaded-images {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
}

.image-item {
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #333;
}

.uploaded-image {
  position: relative;
  width: 100%;
  height: 180px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #333;
  margin-top: 8px;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.remove-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0,0,0,0.7);
  color: white;
  border: none;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.remove-btn:hover {
  background: #ff4757;
  transform: scale(1.1);
}

@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
  }
  .card {
    padding: 20px;
  }
  .image-item {
    width: 100px;
    height: 100px;
  }
  .uploaded-image {
    height: 150px;
  }
}
</style>